import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ShoppingBag as OrderIcon,
} from '@mui/icons-material';
import PurchaseOrderService from '../../services/purchase-order.service';

const PurchaseOrderList: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await PurchaseOrderService.getAll({
        populate: ['supplier', 'requisition', 'createdBy'],
        'pagination[pageSize]': 100,
        sort: 'orderDate:desc',
      });
      setOrders(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this purchase order?')) {
      try {
        await PurchaseOrderService.delete(id);
        fetchOrders();
      } catch (error) {
        console.error('Error deleting purchase order:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'received':
        return 'success';
      case 'confirmed':
        return 'primary';
      case 'sent':
        return 'info';
      case 'partiallyReceived':
        return 'warning';
      case 'draft':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'OMR 0.00';
    return `OMR ${amount.toFixed(2)}`;
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <OrderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Purchase Orders
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/purchase-orders/create')}
        >
          New Purchase Order
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>Expected Delivery</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No purchase orders found
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => {
                const attrs = order.attributes;
                const supplier = attrs?.supplier?.data?.attributes;

                return (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {attrs?.orderNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{supplier?.companyName || supplier?.name || '-'}</TableCell>
                    <TableCell>
                      {new Date(attrs?.orderDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {attrs?.expectedDelivery ? new Date(attrs.expectedDelivery).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        {formatCurrency(attrs?.total)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.status || 'draft'}
                        color={getStatusColor(attrs?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/purchase-orders/${order.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(order.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PurchaseOrderList;

